export interface KBSubsection {
  id: string;
  title: string;
  content: string;
}

export interface KBSection {
  id: string;
  title: string;
  route?: string;
  routes?: string[];
  keywords: string[];
  content: string;
  subsections?: KBSubsection[];
}

export const KNOWLEDGE_BASE: KBSection[] = [
  {
    id: 'platform-overview',
    title: 'Edison — Platform Overview',
    keywords: ['edison', 'rdti', 'r&d', 'division 355', 'tax incentive', 'claim', 'overview', 'getting started'],
    content: `Edison captures engineering evidence continuously throughout the income year and synthesises it into AusIndustry-defensible R&DTI claim outputs under Division 355 of the ITAA 1997.

## Core modules

- **Projects** — organise R&D work by project and income year
- **TFA Panel** — the Technical Feasibility Assessment that gates classification
- **Activities** — specific R&D activities within each project
- **Evidence Vault** — artefacts (commits, transcripts, documents) that prove R&D activity
- **Signals** — automated evidence capture from GitHub
- **Timewriting** — classifying and approving time entries
- **Claims** — finalising and locking your R&DTI claim
- **Audit Portal** — scoped access for external auditors`,
    subsections: [
      {
        id: 'getting-started',
        title: 'Getting started step-by-step',
        content: `1. Go to **Projects** and click **New Project**
2. Set the income year (e.g. FY2025), start date, and name
3. Open the project and click **Start TFA Panel** to begin the Technical Feasibility Assessment
4. Complete all five TFA stages and **Lock** the panel
5. Add R&D **Activities** under the project
6. Connect your GitHub repo under **Signals** to start capturing commit evidence
7. Review time entries in **Timewriting** and bulk-approve those with confidence > 85%
8. When ready, create a **Claim**, validate it, and lock it for lodgement

> **Tip:** Evidence accumulates automatically once GitHub is connected. You don't need to manually upload commits.`,
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    route: '/dashboard',
    keywords: ['dashboard', 'overview', 'metrics', 'progress', 'summary', 'home'],
    content: `The Dashboard gives you a high-level view of your R&DTI claim progress across all projects for the current income year.

## What you see

- Active projects and their TFA status
- Evidence counts per project
- Timewriting review queue status
- Claim progress (draft → validating → locked)

> **Note:** All numbers are real — Edison never shows fabricated data. If a metric shows 0, it means no data has been captured yet.

Use the quick links to jump to any section directly from the dashboard.`,
  },
  {
    id: 'projects',
    title: 'Projects',
    route: '/projects',
    routes: ['/projects/[id]'],
    keywords: ['project', 'income year', 'fy2025', 'create', 'status', 'archived', 'active'],
    content: `Projects are the top-level container for your R&D work. Each project maps to a single income year and a set of related R&D activities.

## Creating a project

1. Click **New Project** in the top-right
2. Enter the project name, income year (e.g. FY2025), and start date
3. Optionally add an end date and description
4. Click **Create**

---

## Project statuses

- **Active** — accepts new evidence and time entries
- **Archived** — read-only, claim locked

> **Note:** Archive a project only once the claim is permanently locked. Archiving is reversible by an admin.

---

## Project detail page

The project detail page shows: TFA panel status, activities list, evidence summary, and signal sources connected to the project.`,
    subsections: [
      {
        id: 'project-lifecycle',
        title: 'Project lifecycle',
        content: `A project moves through these phases in order:

1. **Created** — project exists, no TFA yet
2. **TFA in progress** — panel opened, working through 5 stages
3. **TFA locked** — classification is now permitted
4. **Evidence accumulating** — commits, transcripts being captured
5. **Timewriting review** — time entries being classified and approved
6. **Claim locked** — final output ready for lodgement`,
      },
    ],
  },
  {
    id: 'tfa-panel',
    title: 'TFA Panel — Technical Feasibility Assessment',
    route: '/projects/[id]',
    keywords: ['tfa', 'technical feasibility', 'assessment', 'panel', 'stage', 'determination', 'lock', 'assessor', 'panelist', 'deliberation', 'cots', 'problem definition', 'synthesis', 'systematic investigation', 'technical uncertainty', 'new knowledge', 'innovative', 'workbench', 'yes no', 'eligible', 'ineligible'],
    content: `The Technical Feasibility Assessment (TFA) is the formal process that determines whether your project's activities constitute eligible R&D under Division 355. **The TFA must be locked before Edison can classify any time entries.**

## Five stages

Complete these in order — you cannot skip ahead:

1. **Setup** — add panel members (assessors with the relevant technical expertise)
2. **Problem Definition** — document the technical problems the project addresses
3. **COTS Sweep** — demonstrate no commercially available solution exists
4. **Panel Deliberation** — panel members review and confirm their participation
5. **Determination** — final eligible/ineligible determination, then lock

---

## Locking the TFA

Once all panelists have confirmed and the determination is made, click **Lock TFA**.

> **Important:** Locking is permanent — a locked TFA cannot be edited. Ensure all stages are complete and accurate before locking.

---

## How Edison uses the TFA

Edison classifies time entries against the TFA scope, not as a general R&D assessor. Classification asks: *"does this commit relate to the problem defined in the TFA?"* — not *"is this R&D?"*`,
    subsections: [
      {
        id: 'tfa-panelists',
        title: 'Adding panelists',
        content: `Panelists must be users with accounts in Edison. They are added during the **Setup** stage. Each panelist must confirm their participation during **Panel Deliberation** before the determination can be made.

> **Important:** The **Role on Activity** and **Uncertainty Basis** fields in competence records must always be written in the assessor's own words — Edison never pre-fills or suggests these fields. AusIndustry requires this to be the assessor's genuine attestation.`,
      },
      {
        id: 'tfa-deliberation-workbench',
        title: 'Panel Deliberation — the workbench',
        content: `During **Panel Deliberation**, each panelist works through five questions about the project's R&D eligibility:

| Question | What it tests |
|----------|---------------|
| **Systematic Investigation** | Does the work involve a defined procedure or structured experimentation? |
| **Technical Uncertainty** | Was the outcome unknown in advance to a competent professional? |
| **New Knowledge** | Is the work directed at generating new facts, relationships, or principles? |
| **Innovation** | Does it go beyond routine application of established techniques? |
| **Project-Specific Uncertainty** | Is the uncertainty specific to this project, not general industry-wide uncertainty? |

Each panelist answers **Yes** or **No** to all five questions, then adds an overall comment. Click **Submit my deliberation** when done.

**You can edit your deliberation after submitting.** The button changes to **Update my deliberation** and the submission timestamp shows when it was last edited. Editing remains available until the TFA is locked.

---

## Determination and synthesis

Once all panelists have submitted, the **Determination** stage shows:

- **Panel Assessment Summary** — an AI-generated synthesis of all panelist responses, highlighting consensus and any divergent views
- **Panel Responses** — a compact grid showing each panelist's individual answers with tick/cross pills for each criterion

The synthesising AI reads only the panel's own responses — it does not make an independent eligibility judgment. Use the synthesis to inform the final determination, then record the outcome (Eligible / Ineligible / Certain) and lock the TFA.`,
      },
    ],
  },
  {
    id: 'activities',
    title: 'Activities',
    route: '/projects/[id]',
    keywords: ['activity', 'competence', 'ip ownership', 'uncertainty', 'roleOnActivity', 'uncertaintyBasis', 'eligible', 'ineligible'],
    content: `Activities are the specific R&D tasks within a project. Each activity must be linked to the TFA's problem definition and have at least one competence record from a qualified assessor.

## Creating an activity

1. Open the project and click **Add Activity**
2. Name the activity and describe it
3. Set **IP Ownership** (Company / Client / Joint / Licensed Back / Undetermined)
4. Add competence records for each assessor involved

---

## IP Ownership

| Setting | Effect |
|---------|--------|
| **Company** | No flags — standard eligible activity |
| **Client** | HIGH RISK — flagged in amber, blocks claim locking |
| **Undetermined** | Activity cannot be included in a claim until resolved |
| **Joint / Licensed Back** | Eligible with caveats — review with your advisor |

---

## Competence records

Each record captures the assessor's qualifications (extracted from their LinkedIn/CV profile), their specific role on this activity, and the basis for technical uncertainty.

> **Important:** The role and uncertainty basis **must be typed by the assessor** — these fields are never AI-generated. AusIndustry treats these as direct attestations by the competent professional.`,
  },
  {
    id: 'evidence-vault',
    title: 'Evidence Vault',
    route: '/artefacts',
    keywords: ['evidence', 'artefact', 'vault', 'commit', 'transcript', 'meeting', 'upload', 'link', 'document', 'prd'],
    content: `The Evidence Vault stores all artefacts that prove R&D activity — GitHub commits, meeting transcripts, PRD documents, LLM session logs, and more.

## Artefact types

- \`github_commit\`, \`github_pr\`, \`github_issue\` — ingested automatically via Signals
- \`meeting_transcript\` — upload as a text file or paste content
- \`prd_document\`, \`llm_session\` — paste content directly
- \`manual_note\`, \`file_upload\` — create or upload from the vault

---

## Adding evidence

1. **GitHub commits** — connect a signal source under Signals; commits are ingested automatically
2. **Meeting transcripts** — click **Add Artefact** → paste or upload the transcript text
3. **Manual notes** — click **Add Artefact** → select Manual Note and write your note

---

## Linking evidence to activities

Each artefact can be linked to one or more activities. Links are used during claim validation to demonstrate that evidence supports specific R&D activities.

---

## Content hash

Edison computes a SHA-256 hash of each artefact's content for tamper detection.

> **Note:** If a hash mismatch is detected, the artefact may have been modified after ingestion. Contact your admin.`,
  },
  {
    id: 'signals',
    title: 'Signals — GitHub Integration',
    route: '/signals',
    keywords: ['github', 'webhook', 'signal', 'abandoned work', 'backfill', 'commit', 'repo', 'repository', 'connect'],
    content: `Signals are automated evidence sources. Edison supports GitHub webhooks, which capture commits, pull requests, and issues as they happen.

## Connecting a GitHub repo

1. Go to **Signals** and click **Add Signal Source**
2. Select the project, enter the repo owner and name
3. Install the Edison GitHub App on the repository
4. Click **Save** — Edison will start ingesting new commits immediately
5. To ingest historical commits, click **Backfill**

---

## Abandoned work detection

Edison's async worker analyses your commit history for patterns that suggest abandoned technical work — a key signal of genuine R&D uncertainty. These are surfaced in the **Abandoned Work** queue for review.

---

> **Note:** Signal ingestion is asynchronous — commits appear in the Evidence Vault within a few minutes of being pushed. If they don't appear after 10 minutes, try clicking **Backfill**.`,
  },
  {
    id: 'timewriting',
    title: 'Timewriting',
    route: '/timewriting',
    keywords: ['time', 'hours', 'time entry', 'bulk approve', 'classification', 'rd percentage', 'confidence', '85%', 'review', 'pending', 'approved', 'rejected'],
    content: `Timewriting is where GitHub commit time is reviewed and classified as R&D or non-R&D against the locked TFA scope.

## How classification works

1. Edison ingests commits and groups them into time entries per developer per day
2. The AI classifies each entry against the TFA scope: \`rd\` / \`non_rd\` / \`mixed\`, with a confidence score
3. Entries with **confidence > 85%** are eligible for bulk approval
4. Entries with **confidence ≤ 85%** require individual review

---

## Bulk approval

Select all eligible entries (confidence > 85%) and click **Approve All**. This approves in one action and is the most efficient way to process your queue.

---

## Status values

| Status | Meaning |
|--------|---------|
| \`pending\` | Not yet classified |
| \`classifying\` | AI classification in progress |
| \`approved\` | Reviewed and approved for the claim |
| \`rejected\` | Excluded from the claim (kept for audit) |

> **Note:** Time entries are **never deleted** — rejected entries are kept in the audit trail.

---

## R&D percentage

Each approved entry has an R&D percentage (0–100) that represents what proportion of that time was spent on eligible R&D. This feeds directly into expenditure calculations in your claim.`,
    subsections: [
      {
        id: 'time-entry-review',
        title: 'Reviewing individual entries',
        content: `For entries requiring individual review (confidence ≤ 85%):

1. Click the entry to expand it
2. Review the AI classification and rationale
3. Check the work bucket breakdown — which commits contributed and why
4. Adjust the R&D percentage if needed (enter any value 0–100)
5. Click **Approve** or **Reject**

> **Tip:** You can override the AI's R&D percentage at any time. Your manual review always takes precedence.`,
      },
    ],
  },
  {
    id: 'claims',
    title: 'Claims',
    route: '/claims',
    keywords: ['claim', 'lock', 'validation', 'expenditure', 'schedule 1', 'ato', 'lodgement', 'draft', 'validating', 'locked', 'cost'],
    content: `Claims are the final output of the R&DTI process. A claim aggregates all approved time entries, activities, and expenditure into an ATO Schedule 1 Part A output.

## Claim lifecycle

**Draft** → **Validating** → **Locked**

---

## Creating a claim

1. Click **New Claim** and select the income year
2. Add projects to the claim
3. Add non-labour R&D costs under **Expenditure** (cloud costs, software licences, etc.)

---

## Validation checklist

Before locking, Edison runs a validation checklist. Common blockers:

- TFA not locked for a project
- Activity with IP ownership = Client (high risk flag)
- Activity with IP ownership = Undetermined
- Time entries still pending review

---

## Locking

Once all checklist items pass, click **Lock Claim**.

> **Important:** Locking is permanent. The locked claim generates the ATO Schedule 1 Part A output ready for lodgement.`,
  },
  {
    id: 'audit-portal',
    title: 'Audit Portal',
    route: '/audit-portal',
    keywords: ['audit', 'auditor', 'access token', '90 days', 'portal', 'findings', 'external', 'third party', 'advisor'],
    content: `The Audit Portal gives external auditors, advisors, or due-diligence reviewers scoped read-only access to a specific claim's evidence.

## Creating an access token

1. Go to **Users** → your user → **Access Tokens** (admin only)
2. Select the claim, assign a role (Auditor / Advisor / Due Diligence), and set an expiry
3. Copy the token — it is shown **once only** and cannot be retrieved again
4. Share the token and the portal URL with the external reviewer

> **Important:** Tokens expire automatically at the date you set. Maximum expiry is **90 days**. Revoke a token early from the admin panel if needed.

---

## What auditors see

- Evidence artefacts linked to the claim
- Activities and competence records
- Timewriting summary
- They can add **findings** (flagged comments) for your team to review

---

## Security

All portal actions are audit-logged with a tamper-proof hash chain. The portal has no access to any data outside the specific claim the token was issued for.`,
  },
  {
    id: 'grants',
    title: 'Grants',
    route: '/grants',
    keywords: ['grant', 'funding', 'arc', 'business.gov', 'match', 'discovery', 'application', 'eligibility'],
    content: `The Grants module discovers and matches government funding opportunities relevant to your organisation's R&D profile.

## How it works

1. Complete your **Organisation Profile** (legal name, ABN, industry, description)
2. Edison searches grant databases (ARC, business.gov, state programs) for open opportunities
3. Each grant is scored against your profile for relevance
4. Click any grant to see the match rationale and an AI-generated application aid

---

## Grant statuses

| Status | Meaning |
|--------|---------|
| **Open** | Applications currently accepted |
| **Forecast** | Upcoming — not yet open |
| **Closed** | Deadline passed |
| **Archived** | No longer active |

> **Note:** Grant matching is a discovery aid, not a guarantee of eligibility. Always verify eligibility criteria directly with the funding body before applying.`,
  },
  {
    id: 'users',
    title: 'Users & Roles',
    route: '/users',
    keywords: ['user', 'role', 'admin', 'member', 'viewer', 'tenant', 'invite', 'permission', 'manage'],
    content: `Edison has four roles with descending privilege:

| Role | Access |
|------|--------|
| **tenant_admin** | Full access — manage users, roles, all data |
| **r_and_d_manager** | Manage projects, TFA, activities, claims |
| **member** | Add evidence, submit time entries, view most data |
| **viewer** | Read-only access |

---

## Inviting users

1. Go to **Users** → click **Invite User**
2. Enter their email address and select a role
3. They receive a Clerk invitation and sign in with Google SSO

---

## Changing roles

Admins can change any user's role from the Users page at any time.

> **Note:** Changing a user's role takes effect immediately on their next page load.`,
  },
  {
    id: 'keyboard-shortcuts',
    title: 'Keyboard Shortcuts',
    keywords: ['keyboard', 'shortcut', 'cmd', 'ctrl', 'hotkey', 'quick'],
    content: `Edison supports the following keyboard shortcuts:

| Shortcut | Action |
|----------|--------|
| \`Enter\` | Send message (in chat) |
| \`Shift + Enter\` | New line in chat without sending |
| \`Cmd / Ctrl + Enter\` | Submit comment (in feedback) |
| \`Escape\` | Close panels |

More shortcuts will be added as the platform grows.`,
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    keywords: ['error', 'problem', 'not working', 'fix', 'broken', 'help', 'issue', 'bug', 'fail'],
    content: `Common issues and how to fix them.

## GitHub commits not appearing

- Check the signal source is connected and the Edison GitHub App is installed on the repo
- Try clicking **Backfill** on the signal source
- Commits appear within a few minutes — wait and refresh

## Classification not running

- Verify the TFA is locked for the project (classification requires a locked TFA)
- Check the project has at least one activity

## Claim validation failing

- Review the validation checklist on the claim detail page
- Common causes: unlocked TFA, undetermined IP ownership, pending time entries

## TFA cannot be locked

- All panelists must confirm participation in the Panel Deliberation stage
- The Determination stage must be completed

## Can't see an artefact

- It may have been soft-deleted — check with your admin
- Confirm you have at least member-level access

---

> **Still stuck?** Use the chat below to describe what you're seeing — Edison AI will help diagnose the issue or log a bug report for the team.`,
  },
];
